<!--
  Accusé de réception de commande — bouton du bloc Document de la fiche devis
  (écran admin et écran broker). Remplace « Mail statut accepté ».

  Visible quand le devis est Approved, actif seulement s'il est définitivement
  approuvé : un devis passé en Approved par son broker attend la validation de
  Denis. Le clic ouvre l'aperçu rendu par le serveur (GET /api/orderack/:id) :
  destinataires (client, broker en copie), objet, mail tel qu'il partira, et ce
  qui bloque. « Envoyer » régénère le PDF du devis dans le navigateur ; le
  serveur le dépose comme PDF du devis et le joint (Devis_<n°>.pdf).
-->
<template>
  <div v-if="isApproved">
    <vs-button
      icon-pack="feather" icon="icon-mail" class="mr-4 mb-1 w-full"
      :color="sent.sent_at ? 'success' : 'primary'"
      :disabled="pendingValidation"
      @click="open">Order acknowledgment MAIL
    </vs-button>
    <div class="oa-state mb-4">
      <span v-if="pendingValidation">Awaiting Denis's validation: the acknowledgment cannot be sent yet.</span>
      <span v-else-if="sent.sent_at">
        Sent on {{ formatDateTime(sent.sent_at) }}<span v-if="sent.sent_by"> by {{ sent.sent_by }}</span><span v-if="sent.sent_mode === 'test'"> (TEST)</span>
      </span>
      <span v-else>Not sent to the client yet.</span>
    </div>

    <vs-popup fullscreen title="Order acknowledgment" :active.sync="popupActive">
      <div v-if="loading" class="mb-4">Preparing the preview…</div>
      <vs-alert v-if="error" color="danger" active="true" class="mb-4">{{ error }}</vs-alert>

      <template v-if="ack && !loading">
        <vs-alert v-if="ack.mode === 'test'" color="warning" active="true" class="mb-4">
          TEST MODE (ORDER_ACK_TEST_TO): the acknowledgment will only be sent to {{ ack.test_to }}.
          The client and the broker will receive nothing.
        </vs-alert>
        <vs-alert v-if="ack.status.sent_at" color="success" active="true" class="mb-4">
          Already sent on {{ formatDateTime(ack.status.sent_at) }} to {{ ack.status.sent_to }}.
          Sending again will resend the acknowledgment.
        </vs-alert>
        <vs-alert v-for="b in ack.blockers" :key="'b-' + b.code" color="danger" active="true" class="mb-2">
          {{ b.message }}
        </vs-alert>
        <vs-alert v-for="w in ack.warnings" :key="'w-' + w.code" color="warning" active="true" class="mb-2">
          {{ w.message }}
        </vs-alert>

        <table class="oa-meta mt-2 mb-4">
          <tr><th>From</th><td>noreply@acaciabois.com</td></tr>
          <tr><th>Reply To</th><td>{{ joinList(ack.recipients.reply_to) }}</td></tr>
          <tr><th>To</th><td>{{ joinList(ack.recipients.to) }}</td></tr>
          <tr><th>Cc</th><td>{{ joinList(ack.recipients.cc) }}</td></tr>
          <tr v-if="ack.mode === 'test'">
            <th>Outside test</th>
            <td>To {{ joinList(ack.recipients.live_to) }} — Cc {{ joinList(ack.recipients.live_cc) }}</td>
          </tr>
          <tr><th>Subject</th><td>{{ ack.mail.subject }}</td></tr>
          <tr>
            <th>Attachment</th>
            <td>{{ ack.attachment.filename }} <span class="opacity-75">(quotation PDF regenerated when sending)</span></td>
          </tr>
          <tr>
            <th>Language</th>
            <td>{{ ack.lang }}<span v-if="ack.lang_assumed" class="opacity-75"> (guessed from the billing country)</span></td>
          </tr>
        </table>

        <iframe class="oa-frame" :srcdoc="ack.mail.html" sandbox="" title="Order acknowledgment preview"></iframe>

        <vs-button
          class="w-full mt-4" icon-pack="feather" icon="icon-send"
          :disabled="sending || ack.blockers.length > 0"
          @click="send">{{ ack.status.sent_at ? "Resend acknowledgment" : "Send acknowledgment" }}
        </vs-button>
      </template>
    </vs-popup>
  </div>
</template>

<script>
import moment from "moment";
import axios from "@/axios.js";

export default {
  name: "OrderAckButton",
  props: {
    // La fiche devis telle que chargée par /operation/data/:id (user_data).
    quotation: { type: Object, required: true },
    // GenerateDocumentEN de l'écran : appelé avec `true`, rend le PDF en data-URI.
    generatePdf: { type: Function, required: true },
    // addressSendBlockMessage de l'écran : texte si les adresses empêchent l'envoi.
    sendBlockMessage: { type: Function, default: null },
  },
  data() {
    return {
      popupActive: false,
      loading: false,
      sending: false,
      error: null,
      ack: null,
      sent: this.sentFrom(this.quotation),
    };
  },
  computed: {
    isApproved() {
      const q = this.quotation || {};
      return Number(q.idquotationstatus) === 2 || !!(q.quotationstatus && q.quotationstatus.name_en === "Approved");
    },
    pendingValidation() {
      return !!(this.quotation && this.quotation.pending_admin_validation);
    },
  },
  watch: {
    quotation(q) {
      this.sent = this.sentFrom(q);
    },
  },
  methods: {
    sentFrom(q) {
      const src = q || {};
      return {
        sent_at: src.order_ack_sent_at || null,
        sent_by: src.order_ack_sent_by || null,
        sent_mode: src.order_ack_sent_mode || null,
      };
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
    },
    joinList(list) {
      return list && list.length ? list.join(", ") : "—";
    },
    errorMessage(err) {
      const data = err && err.response && err.response.data;
      return (data && data.message) || (err && err.message) || String(err);
    },
    // Même nom de fichier que les autres dépôts du PDF de ce devis.
    pdfFilename() {
      const q = this.quotation;
      const person = (q.people && q.people[0]) || {};
      const name = person.iscompany
        ? person.company_name
        : [person.firstname, person.lastname].filter(Boolean).join(" ");
      return [(q.operationcategory && q.operationcategory.name_en) || "Quotation", q.id, name || ""].join("_");
    },
    async open() {
      this.popupActive = true;
      this.loading = true;
      this.error = null;
      this.ack = null;
      try {
        const response = await axios.get(`/orderack/${this.quotation.id}`);
        this.ack = response.data;
      } catch (err) {
        this.error = this.errorMessage(err);
      } finally {
        this.loading = false;
      }
    },
    async send() {
      const blockMsg = this.sendBlockMessage ? this.sendBlockMessage() : null;
      if (blockMsg) {
        this.$vs.notify({ color: "danger", title: "Sending blocked", text: blockMsg, time: 8000 });
        return;
      }
      this.sending = true;
      this.error = null;
      this.$vs.loading();
      try {
        const dataUri = await this.generatePdf(true);
        if (!dataUri || String(dataUri).indexOf("base64,") === -1) {
          throw new Error("The quotation PDF could not be generated.");
        }
        const response = await axios.post(`/orderack/${this.quotation.id}/send`, {
          fileBase64: String(dataUri).split("base64,")[1],
          filename: this.pdfFilename(),
        });
        this.ack = response.data.ack || this.ack;
        if (this.ack) {
          this.sent = {
            sent_at: this.ack.status.sent_at,
            sent_by: this.ack.status.sent_by,
            sent_mode: this.ack.status.sent_mode,
          };
        }
        this.$vs.notify({
          color: "success",
          title: "Order acknowledgment sent",
          text: `To ${response.data.to}${response.data.cc ? ` — Cc ${response.data.cc}` : ""}`,
          time: 8000,
        });
        this.popupActive = false;
        this.$emit("sent", this.sent);
      } catch (err) {
        this.error = this.errorMessage(err);
        this.$vs.notify({ color: "danger", title: "Acknowledgment NOT sent", text: this.error, time: 10000 });
      } finally {
        this.sending = false;
        this.$vs.loading.close();
      }
    },
  },
};
</script>

<style scoped>
.oa-state {
  font-size: 0.8rem;
  opacity: 0.8;
}
.oa-meta th {
  text-align: left;
  padding: 3px 16px 3px 0;
  white-space: nowrap;
  vertical-align: top;
  font-weight: 600;
}
.oa-meta td {
  padding: 3px 0;
}
.oa-frame {
  width: 100%;
  max-width: 760px;
  height: 900px;
  border: 1px solid #ddd;
  background: #fff;
}
</style>
